import { useBoolean, useToast } from '@chakra-ui/react'
import { format, isValid, parseISO, subMonths } from 'date-fns'
import { useCallback, useMemo, useRef, useState } from 'react'

import useFilters from '@hook/shared/use-filters'

import { exportToCSV } from '@helper/export/csvUtils'
import {
  IRecomendacaoComprasFiltro,
  IPagination,
  getRecomendacaoCompras
} from '@service/controlefornecedor'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'

export interface IRecomendacaoComprasRow {
  id: number
  data: string
  usuarioId: number
  compradorNome: string
  compradorEmail: string
  empresaId: number
  empresaNome: string
  lojaId: number
  lojaNome: string
  produtoId: number
  produtoNome: string
  fornecedorId: number
  fornecedorNome: string
  mundoId?: number
  mundoNome?: string
  categoriaCompactaId?: number
  categoriaCompactaNome?: string
  categoriaId?: number
  categoriaNome?: string
  subCategoriaId?: number
  subCategoriaNome?: string
  curvaABCD?: string
  curvaABCDEmpresa?: string
  status?: string
  grupoSeparacao?: string
  loteMultiplo?: number
  gerenteProdutoId?: number
  gerenteProdutoDescricao?: string
  recomendacaoQuantidade?: number
  recomendacaoPeso?: number
  recomendacaoVolume?: number
  recomendacaoCusto?: number
  recomendacaoNivelServico?: number
  recomendacaoTipoAlerta?: number
  recomendacaoTipoAlertaDescricao?: string
  compraDesejada?: number
  compraPeso?: number
  compraVolume?: number
  compraCusto?: number
  compraCustoUnitarioFatorConversao?: number
  compraNivelServico?: number
  alteradoSimulacao: boolean
  quantidadeAlteradaSimulacao?: number
  percentualAlteracaoSimulacao?: number | null
  estoqueMaximo?: number
  estoqueCD?: number
  estoqueReservado?: number
  estoqueDisponivel?: number
  estoqueTransitoCompra?: number
  estoqueTransitoTransferencia?: number
  estoqueDeposito16?: number
  estoqueDepositoOutros?: number
  estoqueVendaDiaria?: number
  estoqueVendaMensal?: number
  estoqueVendaProjetada?: number
  estoqueFrequencia?: number
  estoqueLeadTime?: number
  estoqueIncrementoDias?: number
  estoqueIncrementoQuantidade?: number
  estoquePoliticaDeEstoque?: number
  estoquePeriodo?: number
  estoqueSaidaProjetada?: number
  estoqueTransferenciaProjetada?: number
  estoqueSeguranca?: number
  estoqueDias?: number
  estoqueUnidadeFornecedor?: string
  estoqueFatorConversao?: number
  coberturaAlvo?: number
  coberturaAtual?: number
  coberturaTransito?: number
  coberturaDeposito16?: number
  coberturaSugestao?: number
  coberturaDesejada?: number
  coberturaDiferenca?: number
}

export interface IPaginationResponse {
  actualPage: number
  rows: number
  totalRows: number
  totalPages: number
  skip: number
  paginate: boolean
}

// Resposta de v1/RecomendacaoCompras. O fallback PascalCase é defensivo:
// alguns endpoints serializam com a convenção do .NET
interface IRecomendacaoComprasPagination {
  actualPage?: number
  ActualPage?: number
  rows?: number
  Rows?: number
  totalRows?: number
  TotalRows?: number
  totalPages?: number
  TotalPages?: number
  skip?: number
  Skip?: number
  paginate?: boolean
  Paginate?: boolean
}

interface IRecomendacaoComprasResponse {
  success?: boolean
  Success?: boolean
  message?: string
  Message?: string
  data?: IRecomendacaoComprasRow[]
  Data?: IRecomendacaoComprasRow[]
  pagination?: IRecomendacaoComprasPagination
  Pagination?: IRecomendacaoComprasPagination
}

const ROWS_PER_PAGE = 50
const EXPORT_ROWS_PER_PAGE = 500
const MAX_EXPORT_PAGES = 200

const CURVAS_ABCD_OPTIONS = [
  { label: 'A', value: 'A' },
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'D', value: 'D' }
]

const SOMENTE_ALTERADOS_OPTIONS = [
  { label: 'Todos', value: 'todos' },
  { label: 'Somente alterados', value: 'true' },
  { label: 'Somente inalterados', value: 'false' }
]

// Mesma tabela de typesOfAlerts() do FiltersProvider
// (src/providers/Shared/FiltersProvider.tsx) — copiada pois não é exportada
const TIPOS_ALERTA_OPTIONS = [
  { label: 'Estoque Arbitrado', value: 10 },
  { label: 'Compra Mínima', value: 11 },
  { label: 'Produto Similar', value: 12 },
  { label: 'Poucos dias de venda', value: 13 },
  { label: 'Produto antigo e venda baixa', value: 14 },
  { label: 'Ok para compra', value: 15 },
  { label: 'Estoque Baixo CD', value: 16 },
  { label: 'Estoque Mínimo', value: 17 },
  { label: 'Incremento Estoque', value: 18 }
]

const DEFAULT_FILENAME = 'Pedidos / SKUs do Cronograma'

const toLocalDate = (d: Date) => format(d, 'yyyy-MM-dd')

// Período padrão: último mês até hoje
const getDefaultDates = () => {
  const today = new Date()
  return {
    initialDate: toLocalDate(subMonths(today, 1)),
    finalDate: toLocalDate(today)
  }
}

const getErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message ??
  err?.response?.data?.Message ??
  err?.message ??
  fallback

const parseResponse = (res: IRecomendacaoComprasResponse | undefined) => ({
  success: res?.success ?? res?.Success ?? false,
  message: res?.message ?? res?.Message,
  items: Array.isArray(res?.data ?? res?.Data)
    ? ((res?.data ?? res?.Data) as IRecomendacaoComprasRow[])
    : [],
  pagination: res?.pagination ?? res?.Pagination
})

const normalizeRow = (item: any): IRecomendacaoComprasRow => ({
  ...item,
  alteradoSimulacao: item.alteradoSimulacao ?? item.AlteradoSimulacao ?? false
})

const formatRowDate = (value?: string | null) => {
  if (!value) return ''
  const date = parseISO(value)
  return isValid(date) ? format(date, 'dd/MM/yyyy') : ''
}

const buildApiFilter = (params: any): IRecomendacaoComprasFiltro => {
  const defaults = getDefaultDates()
  const toIso = (d: string, fallback: string, time: string) => {
    const value = d || fallback
    return value.includes('T') ? value : `${value}T${time}`
  }

  return {
    dataInicio: toIso(params.initialDate, defaults.initialDate, '00:00:00'),
    dataFim: toIso(params.finalDate, defaults.finalDate, '23:59:59'),
    empresasIds: params.companies?.map((item: any) => item.value) || [],
    lojasIds: params.centers?.map((item: any) => item.value) || [],
    fornecedoresIds:
      params.providersForPurchase?.map((item: any) => item.value) || [],
    categoriasCompactasIds:
      params.compactCategories?.map((item: any) => item.value) || [],
    mundosIds: params.worlds?.map((item: any) => item.value) || [],
    produtosIds: params.produtosIds?.map((item: any) => item.value) || [],
    compradoresIds:
      params.compradores
        ?.map((item: any) => item.value)
        .filter((value: any) => value != null) || [],
    curvasABCD: params.curvasABCD?.map((item: any) => item.value) || [],
    tiposAlerta: params.tiposAlerta?.map((item: any) => item.value) || [],
    // select-pagination com isMulti=false devolve a opção selecionada (objeto)
    somenteAlterados:
      !params.somenteAlterados || params.somenteAlterados?.value === 'todos'
        ? null
        : params.somenteAlterados?.value === 'true'
  }
}

export default function usePedidosCronograma() {
  const { getFilters } = useFilters()
  const toast = useToast()

  const [data, setData] = useState<IRecomendacaoComprasRow[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [params, setParams] = useState<any>(getDefaultDates)
  // Filtros efetivamente aplicados (último "Prosseguir"): usados na
  // paginação e no export, para não misturar com edições não aplicadas
  const [appliedParams, setAppliedParams] = useState<any>(null)
  const [filters, setFilters] = useBoolean(true)
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState(DEFAULT_FILENAME)

  const requestIdRef = useRef(0)

  const [pagination, setPagination] = useState<IPaginationResponse>({
    actualPage: 1,
    rows: ROWS_PER_PAGE,
    totalRows: 0,
    totalPages: 0,
    skip: 0,
    paginate: true
  })

  const handleUpdateParams = useCallback(
    ({ name, value }: { name: string; value: any }) => {
      setParams((prev: any) => {
        // Limpar o campo de data volta ao padrão (nunca envia data vazia)
        if ((name === 'initialDate' || name === 'finalDate') && !value) {
          return { ...prev, [name]: getDefaultDates()[name] }
        }
        return { ...prev, [name]: value }
      })
    },
    []
  )

  const loadPage = useCallback(async (sourceParams: any, page: number) => {
    const requestId = ++requestIdRef.current
    const isStale = () => requestId !== requestIdRef.current

    setLoading(true)
    setError(null)
    try {
      const filtro = buildApiFilter(sourceParams)
      const pag: IPagination = { actualPage: page, rows: ROWS_PER_PAGE }
      const { data: res } = await getRecomendacaoCompras(filtro, pag)
      if (isStale()) return

      const parsed = parseResponse(res as IRecomendacaoComprasResponse)
      if (!parsed.success) {
        throw new Error(parsed.message || 'Erro ao buscar dados')
      }

      setData(parsed.items.map(normalizeRow))

      const p = parsed.pagination
      setPagination({
        actualPage: p?.actualPage ?? p?.ActualPage ?? page,
        rows: p?.rows ?? p?.Rows ?? ROWS_PER_PAGE,
        totalRows: p?.totalRows ?? p?.TotalRows ?? parsed.items.length,
        totalPages: p?.totalPages ?? p?.TotalPages ?? 1,
        skip: p?.skip ?? p?.Skip ?? 0,
        paginate: p?.paginate ?? p?.Paginate ?? true
      })
    } catch (err: any) {
      if (isStale()) return
      setError(getErrorMessage(err, 'Erro ao carregar dados'))
      setData([])
      setPagination(prev => ({
        ...prev,
        actualPage: 1,
        totalRows: 0,
        totalPages: 0
      }))
    } finally {
      if (!isStale()) setLoading(false)
    }
  }, [])

  // "Prosseguir" do painel de filtros: aplica os filtros e busca a 1ª página
  const applyFilters = useCallback(() => {
    setAppliedParams(params)
    loadPage(params, 1)
  }, [params, loadPage])

  const handlePageChange = useCallback(
    (selected: number) => {
      if (!appliedParams) return
      loadPage(appliedParams, selected + 1)
    },
    [appliedParams, loadPage]
  )

  const clearAllFilters = useCallback(() => {
    requestIdRef.current++
    setLoading(false)
    setParams(getDefaultDates())
    setAppliedParams(null)
    setData([])
    setError(null)
    setQuery('')
    setPagination(prev => ({
      ...prev,
      actualPage: 1,
      totalRows: 0,
      totalPages: 0
    }))
  }, [])

  // Busca local: atua somente sobre a página carregada
  const filtered = useMemo(() => {
    if (!query) return data
    const q = query.toLowerCase()
    return data.filter(
      row =>
        (row.fornecedorNome ?? '').toLowerCase().includes(q) ||
        (row.compradorEmail ?? '').toLowerCase().includes(q) ||
        (row.compradorNome ?? '').toLowerCase().includes(q) ||
        (row.lojaNome ?? '').toLowerCase().includes(q) ||
        String(row.produtoId).includes(q) ||
        (row.produtoNome ?? '').toLowerCase().includes(q)
    )
  }, [data, query])

  const dataCards = useMemo(() => {
    const skusDistintos = new Set(data.map(r => r.produtoId)).size
    const totalRecomendado = data.reduce(
      (s, r) => s + (r.recomendacaoQuantidade ?? 0),
      0
    )
    const totalDesejado = data.reduce((s, r) => s + (r.compraDesejada ?? 0), 0)
    const totalAlterados = data.filter(r => r.alteradoSimulacao).length

    return { skusDistintos, totalRecomendado, totalDesejado, totalAlterados }
  }, [data])

  // Exporta TODAS as páginas do filtro aplicado, sem a busca local
  // (a busca e a ordenação atuam só na página exibida)
  const exportCsv = useCallback(async () => {
    if (exporting || !appliedParams) return

    setExporting(true)
    try {
      const filtro = buildApiFilter(appliedParams)
      const allRows: IRecomendacaoComprasRow[] = []
      let truncated = false
      let failure: string | null = null

      for (let page = 1; page <= MAX_EXPORT_PAGES; page++) {
        let parsed: ReturnType<typeof parseResponse>
        try {
          const { data: res } = await getRecomendacaoCompras(filtro, {
            actualPage: page,
            rows: EXPORT_ROWS_PER_PAGE
          })
          parsed = parseResponse(res as IRecomendacaoComprasResponse)
        } catch (err) {
          failure = getErrorMessage(err, 'Erro ao buscar dados')
          break
        }

        if (!parsed.success) {
          failure = parsed.message || 'Erro ao buscar dados'
          break
        }

        allRows.push(...parsed.items.map(normalizeRow))

        const totalPages =
          parsed.pagination?.totalPages ?? parsed.pagination?.TotalPages
        const isLastPage =
          typeof totalPages === 'number'
            ? page >= totalPages
            : parsed.items.length < EXPORT_ROWS_PER_PAGE
        if (isLastPage) break
        if (page === MAX_EXPORT_PAGES) truncated = true
      }

      if (failure && allRows.length === 0) {
        toast({
          title: 'Erro ao exportar',
          description: failure,
          status: 'error',
          duration: 4000,
          isClosable: true
        })
        return
      }

      exportToCSV({
        filename: `${(filename || DEFAULT_FILENAME).trim()}.csv`,
        headers: [
          'SKU-ID',
          'SKU-Nome',
          'Fornecedor-ID',
          'Fornecedor',
          'Comprador',
          'Centro-ID',
          'Centro',
          'Qtd Recomendada',
          'Qtd Desejada',
          'Alteração',
          '%',
          'Tipo Alerta',
          'Data'
        ],
        data: allRows.map(r => ({
          produtoId: r.produtoId,
          produtoNome: r.produtoNome,
          fornecedorId: r.fornecedorId,
          fornecedorNome: r.fornecedorNome,
          comprador: r.compradorNome || r.compradorEmail,
          lojaId: r.lojaId,
          lojaNome: r.lojaNome,
          recomendacaoQuantidade: r.recomendacaoQuantidade,
          compraDesejada: r.compraDesejada,
          quantidadeAlteradaSimulacao: r.quantidadeAlteradaSimulacao,
          percentual:
            r.percentualAlteracaoSimulacao != null
              ? `${r.percentualAlteracaoSimulacao.toFixed(1)}%`
              : '',
          tipoAlerta: r.recomendacaoTipoAlertaDescricao,
          data: formatRowDate(r.data)
        })),
        fieldMapping: [
          'produtoId',
          'produtoNome',
          'fornecedorId',
          'fornecedorNome',
          'comprador',
          'lojaId',
          'lojaNome',
          'recomendacaoQuantidade',
          'compraDesejada',
          'quantidadeAlteradaSimulacao',
          'percentual',
          'tipoAlerta',
          'data'
        ]
      })

      if (failure || truncated) {
        toast({
          title: 'Exportação incompleta',
          description: failure
            ? `${allRows.length} registros exportados. Falha ao buscar as demais páginas: ${failure}`
            : `Exportação limitada a ${allRows.length} registros. Refine os filtros para exportar tudo.`,
          status: 'warning',
          duration: 6000,
          isClosable: true
        })
      } else {
        toast({
          title: 'Exportação concluída',
          description: `${allRows.length} registros exportados`,
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      }
    } catch (err) {
      toast({
        title: 'Erro ao exportar',
        description: getErrorMessage(err, 'Tente novamente mais tarde'),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    } finally {
      setExporting(false)
    }
  }, [exporting, appliedParams, filename, toast])

  const actions = useMemo(
    () => [
      {
        label: exporting ? 'Exportando...' : 'Exportar CSV',
        type: 'button' as const,
        onClick: exportCsv
      }
    ],
    [exportCsv, exporting]
  )

  const filtersList: ILayoutFilter[] = useMemo(
    () => [
      {
        id: 'dateRange',
        label: 'Período',
        type: 'date-input' as const,
        isRequired: true
      },
      {
        id: 'companies',
        label: 'Empresas',
        type: 'select-pagination' as const,
        filterId: 'companies',
        isMulti: true
      },
      {
        id: 'centers',
        label: 'Centros',
        type: 'select-pagination' as const,
        filterId: 'centers',
        isMulti: true
      },
      {
        id: 'providersForPurchase',
        label: 'Fornecedores',
        type: 'select-pagination' as const,
        filterId: 'providersForPurchase',
        isMulti: true
      },
      {
        id: 'compactCategories',
        label: 'Categorias Compactas',
        type: 'select-pagination' as const,
        filterId: 'compactCategories',
        isMulti: true
      },
      {
        id: 'worlds',
        label: 'Mundos',
        type: 'select-pagination' as const,
        filterId: 'worlds',
        isMulti: true
      },
      {
        id: 'produtosIds',
        label: 'Produtos',
        type: 'select-api-post' as const,
        apiUrl: '/v1/Produto/Compras',
        isMulti: true
      },
      {
        id: 'compradores',
        label: 'Compradores',
        type: 'select-pagination' as const,
        filterId: 'solicitantes',
        isMulti: true
      },
      // Opções fixas: o branch select-pagination do LayoutFilters usa
      // filter.options (o tipo 'select' ignorava as opções)
      {
        id: 'curvasABCD',
        label: 'Curva ABCD',
        type: 'select-pagination' as const,
        options: CURVAS_ABCD_OPTIONS,
        isMulti: true
      },
      {
        id: 'tiposAlerta',
        label: 'Tipo de Alerta',
        type: 'select-pagination' as const,
        options: TIPOS_ALERTA_OPTIONS,
        isMulti: true
      },
      {
        id: 'somenteAlterados',
        label: 'Alteração',
        type: 'select-pagination' as const,
        options: SOMENTE_ALTERADOS_OPTIONS,
        isMulti: false
      }
    ],
    []
  )

  return {
    data: filtered,
    rawCount: data.length,
    loading,
    exporting,
    error,
    params,
    hasAppliedFilters: !!appliedParams,
    filters,
    setFilters,
    query,
    setQuery,
    filename,
    setFilename,
    filtersList,
    handleUpdateParams,
    applyFilters,
    clearAllFilters,
    actions,
    pagination,
    handlePageChange,
    getFilters,
    dataCards
  }
}
